// src/config/api.ts

/** Centralized API base URLs. */
export interface ApiEndpoints {
    BASE: string;
    YTG: string;
    GATEWAY: string;
}

/** Resolved API base URLs used across every module's API client. */
export const API_ENDPOINTS: ApiEndpoints = {
    BASE: "/api",
    YTG: process.env.REACT_APP_API_YTG || "/api/ytg",
    GATEWAY: "/api",
};

/** Google OAuth + APIs configuration. */
export interface GoogleConfig {
    CLIENT_ID: string | undefined;
    API_KEY: string | undefined;
    /** Google Cloud project number, required by the Picker to grant `drive.file` access. */
    APP_ID: string | undefined;
    /** Only scope the Pickers ask for: per-file access to what the user picks. */
    FILE_SCOPE: string;
}

/**
 * Extract the Cloud project number from an OAuth client ID.
 *
 * Client IDs read `<project number>-<hash>.apps.googleusercontent.com`.
 *
 * @param clientId - Google OAuth client ID
 * @returns The project number, or undefined when the ID is missing or malformed
 */
export const projectNumberFromClientId = (clientId: string | undefined): string | undefined => {
    const match = /^(\d+)-/.exec(clientId ?? "");
    return match ? match[1] : undefined;
};

/**
 * Resolved Google OAuth credentials.
 *
 * The scopes requested at login live in the gateway only. Without `APP_ID`,
 * a file picked under `drive.file` is selected but never granted to the app.
 */
export const GOOGLE_CONFIG: GoogleConfig = {
    CLIENT_ID: process.env.REACT_APP_GOOGLE_CLIENT_ID,
    API_KEY: process.env.REACT_APP_GOOGLE_API_KEY,
    APP_ID: projectNumberFromClientId(process.env.REACT_APP_GOOGLE_CLIENT_ID),
    FILE_SCOPE: "https://www.googleapis.com/auth/drive.file",
};

/** Outcome of {@link validateConfig}: whether config is complete and which keys are missing. */
export interface ValidateConfigResult {
    valid: boolean;
    missing: string[];
}

/**
 * Validate required environment variables.
 *
 * Returns `{ valid, missing }` so the caller can render an explicit
 * "config missing" screen instead of letting the app boot with empty
 * env values (which previously caused the Google SDK to crash silently).
 */
export const validateConfig = (): ValidateConfigResult => {
    const required: Record<string, string | undefined> = {
        GOOGLE_CLIENT_ID: GOOGLE_CONFIG.CLIENT_ID,
        GOOGLE_API_KEY: GOOGLE_CONFIG.API_KEY,
        // Without the project number the Pickers select files but never grant them.
        GOOGLE_APP_ID: GOOGLE_CONFIG.APP_ID,
    };

    const missing = Object.entries(required)
        .filter(([, value]) => !value)
        .map(([key]) => key);

    return { valid: missing.length === 0, missing };
};
